import { hslOf, clamp, wrapHue } from '../utils/color';
import { SubscriptionPlan } from '../utils/subscription';

/**
 * アバターの縁取り。
 *
 * CSS の border はグラデーションを取れないので、アバターの裏に円を敷いて縁だけ見せる。
 * 実際の組み立ては components/profile/AvatarRing.tsx。
 *
 * 保存するのは key だけ。CSS をユーザーデータに入れない（後から見た目を調整できなくなる）。
 */

export type AvatarBorderKey =
  | 'default'
  | 'theme' | 'glow' | 'wave' | 'waveR' | 'holo' | 'spectrum'  // アクセント色から作る
  | 'rainbow' | 'prism';                                        // テーマに依存しない固定色

export interface AvatarBorderPreset {
  key: AvatarBorderKey;
  label: string;
  description: string;
  /** アクセント色に追従するか。UI のグループ分けに使う */
  themed?: boolean;
  /**
   * 選択に必要な subscription_plan の下限。
   * プリセットは有料なら全部使える方針なので PRO(=1) 止まり。
   * MAX(=3) は「プリセットの外に出る」自由入力系のために空けてある。
   * ここは表示制御にすぎない。**サーバー側の検証が別途必要**。
   */
  minPlan: number;
}

export const AVATAR_BORDERS: AvatarBorderPreset[] = [
  { key: 'default', label: 'なし', description: '既定の細い白縁', minPlan: 0 },
  // ここからアクセント色由来
  { key: 'theme', themed: true, label: 'テーマ色', description: 'アクセント色のソリッド', minPlan: 0 },
  { key: 'glow', themed: true, label: 'グロー', description: 'アクセント色＋外側に発光', minPlan: 0 },
  // ウェーブは向きが2つ。片方だけだと、色相の進む先が気に入らないアクセントを
  // 選んだ人に逃げ場が無くなる（例: ライムは -58° だと赤へ、+58° だと緑へ行く）
  { key: 'wave', themed: true, label: 'ウェーブ', description: 'アクセントと隣の色相が入れ替わりながら回る', minPlan: SubscriptionPlan.PRO },
  { key: 'waveR', themed: true, label: 'リバース', description: 'ウェーブの逆向き。反対側の隣接色へ移る', minPlan: SubscriptionPlan.PRO },
  // 箔2種。母材はどちらもアクセント色で、違いは色相の振り幅だけ
  { key: 'holo', themed: true, label: 'ホロ', description: 'アクセント色を母材にした箔', minPlan: SubscriptionPlan.PRO },
  { key: 'spectrum', themed: true, label: 'スペクトラム', description: '色が大きく回る派手な箔', minPlan: SubscriptionPlan.PRO },
  // ここから固定色。テーマを変えても見た目が変わらない組
  { key: 'rainbow', label: 'レインボー', description: '原色の虹が回る', minPlan: SubscriptionPlan.PRO },
  { key: 'prism', label: 'プリズム', description: 'テーマ色に依存しない虹箔', minPlan: SubscriptionPlan.PRO },
];

export const DEFAULT_AVATAR_BORDER: AvatarBorderKey = 'default';

/**
 * 縁の太さ。px ではなくアバター寸法に対する比で持つ。
 * 固定 px にすると 72px のモバイルと 96px の PC、40px のプレビューで印象が変わり、
 * 3段階を並べても差が潰れる（丸めで s と m が同じ px になる）。
 * 端数 px はそのまま渡してよい（背景の描画なのでサブピクセルで問題ない）。
 */
export type AvatarBorderWidthKey = 's' | 'm' | 'l';

// 既定は細い側。そこから2段階だけ太くできる。
// キー(s/m/l)は保存値なので変えないこと。ラベルだけが役割を表す。
export const AVATAR_BORDER_WIDTHS: Array<{ key: AvatarBorderWidthKey; label: string; ratio: number }> = [
  { key: 's', label: '標準', ratio: 0.032 },  // 96px で約3px
  { key: 'm', label: '太め', ratio: 0.05 },
  { key: 'l', label: '太い', ratio: 0.07 },
];

export const DEFAULT_AVATAR_BORDER_WIDTH: AvatarBorderWidthKey = 's';

export const borderWidthRatio = (key: string | undefined): number =>
  AVATAR_BORDER_WIDTHS.find(w => w.key === key)?.ratio
  ?? AVATAR_BORDER_WIDTHS.find(w => w.key === DEFAULT_AVATAR_BORDER_WIDTH)!.ratio;

export const canUseAvatarBorder = (key: string, plan: number): boolean =>
  plan >= (AVATAR_BORDERS.find(b => b.key === key)?.minPlan ?? 0);

/**
 * 保存値を「実際に描画してよい key」に落とす。**描画前に必ず通すこと。**
 * 解約したら使えなくなるが保存値は消さない（再契約で元に戻る）。
 * 判定は profileAccent.ts の resolveAccent と同じ方針。
 */
export const resolveAvatarBorder = (key: string | undefined, plan: number): AvatarBorderKey => {
  const preset = AVATAR_BORDERS.find(b => b.key === key);
  return preset && plan >= preset.minPlan ? preset.key : DEFAULT_AVATAR_BORDER;
};

export interface RingVisual {
  base: string;
  /**
   * base の上に重ねるもう1枚。用途は2つある。
   * - 箔(holo/spectrum): 白い艶。逆回転させて母材の上を滑らせる
   * - ウェーブ2: 山数と速度が違う同じ配色の波。干渉させてうねらせる
   */
  overlay?: string;
  overlayOpacity?: number;
  glow?: string;
  /** 1回転の秒数。負値は逆回転。未指定なら静止 */
  spin?: number;
  overlaySpin?: number;
}

const HOLO_SHEEN =
  'conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.9) 22deg, transparent 55deg, transparent 175deg, rgba(255,255,255,0.6) 200deg, transparent 240deg)';

/**
 * ホロの箔をアクセント色から組む。
 *
 * **差し色は必ずアクセントからの色相差で置くこと。固定の hex にしないこと。**
 * 以前 `#9ee7ff` などの絶対色を混ぜていたが、それだとアクセントとの色相差が
 * 9°〜60° までテーマごとにばらつき、シアンやピンクでは差し色と同化して
 * ただの単色リングになっていた（「テーマによって合う合わないがある」の正体）。
 * 相対で置けば、どのアクセントでも同じ関係＝同じ質感になる。
 *
 * 彩度と明度も幅を絞る。アクセントは L50〜85 まで散っているので、
 * 生の hex をそのまま母材にすると暗い色では濁り、明るい色では白飛びする。
 * 色相だけ受け継いで「箔として成立する帯」に寄せる。
 * 他の場所のアクセント色と厳密には一致しなくなるが、本物の箔も下地の色そのものではない。
 */
// 箔3種の違いは**色相の振り幅だけ**。母材・明度・艶の作り方は共通にしてある。
// 振り幅が広いほど派手になるが、相対で置いてあるのでどのアクセントでも成立する。
// lift は差し色をどれだけ明るく振るか。狭い振り幅ほど明度差に頼るので小さくしない。
// sheenScale は艶の補正。色相差が小さいパールは艶が効きの主役になるので強め。
const FOIL_VARIANTS = {
  holo: { hues: [40, -45, 82], lift: 18, spin: 9, sheenScale: 1 },
  spectrum: { hues: [78, -96, 150], lift: 16, spin: 7, sheenScale: 0.85 },
} as const;

const holoFoil = (accent: string, variant: keyof typeof FOIL_VARIANTS) => {
  const v = FOIL_VARIANTS[variant];
  const { h, s, l } = hslOf(accent);
  const S = clamp(s, 60, 95);
  const L = clamp(l, 54, 70);
  const at = (dh: number, dl = 0) =>
    `hsl(${wrapHue(h + dh)} ${S}% ${clamp(L + dl, 40, 88)}%)`;
  const [a, b, c] = v.hues;
  return {
    // 母材と差し色を交互に。差し色は明るく振って箔の反射に見せる
    base:
      `conic-gradient(from 0deg, ${at(0)} 0%, ${at(a, v.lift)} 17%, ${at(0)} 33%, `
      + `${at(b, v.lift + 2)} 50%, ${at(0)} 66%, ${at(c, v.lift - 2)} 84%, ${at(0)} 100%)`,
    // 明るい母材ほど白い艶が効かないので、そのぶん弱める
    overlayOpacity: clamp((0.62 - (L - 54) * 0.02) * v.sheenScale, 0.28, 0.7),
    glow: `hsl(${h} ${S}% ${L}% / 0.28)`,
    spin: v.spin,
  };
};

export const ringVisual = (key: string, accent: string): RingVisual => {
  switch (key) {
    case 'theme':
      return { base: accent };
    case 'glow':
      return { base: accent, glow: `0 0 10px ${accent}59, 0 0 22px ${accent}2e` };
    // 使う色は2つだけ。移動先は隣接色(±58°)で、どのアクセントでも隣に収まる。
    // wave は色相を戻す向き(紫→青)、waveR は進む向き(紫→ピンク)。
    // 艶は乗せない（箔とは別系統だと分かるように）。
    //
    // 【混ぜないこと・そして動いて見せること】
    // この2つは同じ設計で両立する。**純色の面積を広く取り、遷移帯を狭くする。**
    //  - 山を増やす（repeating-conic）と中間色が全周に出て濁る。一度やって没にした
    //  - 逆に遷移を全周に薄く伸ばす（A 0% / B 50% / A 100%）と回転に対してほぼ対称になり、
    //    どの角度でも同じに見えて「動いていない」ように見える。これも没にした
    // 純色の帯を2つ置いて境目を狭くすると、その境目が追える特徴になって回転が見える。
    // 面積を非対称(A 170° / B 90°)にしてあるのも、どちらが回っているか分かりやすくするため。
    case 'wave':
    case 'waveR': {
      const { h, s, l } = hslOf(accent);
      const S = clamp(s, 62, 95);
      const L = clamp(l, 52, 70);
      const dir = key === 'waveR' ? 58 : -58;
      const from = `hsl(${wrapHue(h)} ${S}% ${L}%)`;
      const to = `hsl(${wrapHue(h + dir)} ${S}% ${clamp(L + 8, 40, 84)}%)`;
      return {
        base:
          `conic-gradient(from 200deg, `
          + `${from} 0deg, ${from} 150deg, `      // 純色A
          + `${to} 195deg, ${to} 285deg, `        // 遷移45°→純色B
          + `${from} 340deg, ${from} 360deg)`,    // 遷移55°→純色Aへ戻る(継ぎ目なし)
        spin: 6,
        glow: `0 0 10px hsl(${h} ${S}% ${L}% / 0.3)`,
      };
    }
    case 'rainbow':
      return {
        base: 'conic-gradient(from 0deg, #ff3b30, #ffcc00, #34c759, #00c7ff, #5856d6, #ff2d95, #ff3b30)',
        spin: 6,
      };
    // ホロは2種類。母材がアクセント色に追従する holo と、テーマから独立した prism。
    // 艶(sheen)の作り方はどちらも同じで、母材の配色だけが違う。
    // 白い帯を**逆回転**させることで箔に見える。同方向だと虹が回るだけでホロにならない。
    case 'holo':
    case 'spectrum': {
      const foil = holoFoil(accent, key);
      return {
        base: foil.base,
        overlay: HOLO_SHEEN,
        overlayOpacity: foil.overlayOpacity,
        spin: foil.spin,
        overlaySpin: -5,
        glow: `0 0 12px ${foil.glow}`,
      };
    }
    case 'prism':
      // こちらはテーマ非依存が売りなので固定色でよい
      return {
        base: 'conic-gradient(from 0deg, #b5f5ff, #ffc8f0 16%, #fff3b0 33%, #c8ffd8 50%, #b8c9ff 66%, #ffb8e0 83%, #b5f5ff)',
        overlay: HOLO_SHEEN,
        overlayOpacity: 0.45,
        spin: 9,
        overlaySpin: -5,
        glow: '0 0 12px rgba(200, 230, 255, 0.35)',
      };
    default:
      return { base: 'rgba(255, 255, 255, 0.25)' };
  }
};
